// Popup.jsx (drop-in replacement for your version)
import React, { useEffect } from "react";
import ReactDOM from "react-dom";

/**
 * Props:
 *  - title
 *  - onClose  (function) - called when modal should be closed
 *  - height, maxWidth, minHeight, displayClose
 *  - children
 *
 * This version:
 *  - uses a portal so modal sits above everything
 *  - locks background scroll while open
 *  - closes on ESC and overlay click
 *  - stops propagation for inner clicks
 *  - supports both onClose() and onClose(false) (keeps backward compatibility)
 */
function Popup({
  title,
  onClose,
  height,
  maxWidth = "800px",
  children,
  minHeight = "600px",
  displayClose = true,
}) {
  // create portal root if not present
  const containerId = "app-popup-root";
  let container = document.getElementById(containerId);
  if (!container) {
    container = document.createElement("div");
    container.id = containerId;
    document.body.appendChild(container);
  }

  // lock body scroll while modal open
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  // ESC to close
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (typeof onClose === "function") {
          // support both onClose() and legacy onClose(false)
          try {
            onClose();
          } catch {
            onClose(false);
          }
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleClose = () => {
    if (typeof onClose === "function") {
      try {
        onClose();
      } catch {
        // in case parent expects onClose(false)
        onClose(false);
      }
    }
  };

  const dialog = (
    <div>
      {/* backdrop */}
      <div
        className="modal-backdrop fade show"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1050,
          backgroundColor: "rgba(0,0,0,0.45)",
        }}
        onMouseDown={handleClose}
      />

      {/* modal */}
      <div
        className="modal fade show"
        role="dialog"
        aria-modal="true"
        style={{
          display: "flex",
          position: "fixed",
          inset: 0,
          zIndex: 1060,
          alignItems: "center",
          justifyContent: "center",
          padding: 10,
        }}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          style={{ maxWidth }}
          onMouseDown={(e) => {
            // prevent backdrop click when interacting inside dialog
            e.stopPropagation();
          }}
        >
          <div
            className="modal-content"
            style={{
              minHeight,
              maxHeight: "100vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div className="modal-header" style={{ flexShrink: 0 }}>
              <h6 className="modal-title">{title}</h6>
              {displayClose && (
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={handleClose}
                />
              )}
            </div>

            <div
              className="container mt-1"
              style={{
                padding: "0.5rem",
                overflowY: "auto",
                flex: 1,
              }}
            >
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(dialog, container);
}

export default Popup;
